import { useCallback, useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_BLOCK_BORDER_COLOR } from '../../../../shared/documentColorDefaults';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import type { BlockSettings } from '../../../../grid/reduxStore/blockStyleSettingsSlice';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import IcoLock from '../../../../../icon/icons/IcoLock';
import IcoLockOpen from '../../../../../icon/icons/IcoLockOpen';

import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';
import {
  EditorDefaultSidePanelType,
  setActiveSidePanel,
  setIsAdvancedSpacingModelOpen,
  setIsReplaceModeActive,
} from '../../../../grid/reduxStore/blockStyleSettingsSlice';

import {
  ColorPickerSection,
  DropDownSection,
  NumberInputBoxSingleCol,
  NumInputBoxTwoCol,
  SecondaryMenu,
  SecondaryMenuSectionTitle,
  SecondaryMenuTrigger,
  TextInputBoxSingleCol,
} from '../../../component';

import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useBlockStyleSettings } from '../../../../hooks/useBlockSettings';

import { getBlockSidesFromString } from 'components/editor/GridDndEditor/Block/Helper/BlockSides';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent';
import { ImagesLayout } from 'components/library/images/ImagesLayout';
import { cleanUrl } from '../../../../../../utils/cleanUrl';
import {
  useAspectRatio,
  DIMENSION_WIDTH,
  DIMENSION_HEIGHT,
} from 'components/editor/SidePanel/content/DesignSettings/common/useAspectRatio';
import { useBufferedState } from 'hooks/useBufferedState';

export function ImageBlockSettings() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    getBlockStyle,
    displayChange,
    changeBlockBorderColor,
    changeBlockBorderSide,
    changeBorderRadius,
    changeBorderWidth,
    changeAspectRatioLock,
    saveCurrentStateToRef,
    blockDesignHandlerWithoutUndoRedo,
  } = useBlockStyleSettings();

  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId);

  const {
    opacity = 0,
    imageAlt = '',
    imageLink = '',
    imageAspectRatioLock = false,
    borderColor = DEFAULT_BLOCK_BORDER_COLOR,
    currentBorderSide = [],
    borderRadius = 0,
    borderWidth = 0,
  } = useMemo(() => getBlockStyle(), [getBlockStyle, selectedBlockIdByWrapper]);

  const width = blockContent?.blockConfig.width || 0;
  const height = blockContent?.blockConfig.height || 0;

  const [localOpacity, setLocalOpacity] = useBufferedState(opacity);
  const [localImageAlt, setLocalImageAlt] = useBufferedState(imageAlt);
  const [localImageLink, setLocalImageLink] = useBufferedState(imageLink);
  const [localBorderWidth, setLocalBorderWidth] = useBufferedState(borderWidth);
  const [localBorderRadius, setLocalBorderRadius] = useBufferedState(borderRadius);
  const [localWidth, setLocalWidth] = useBufferedState(width);
  const [localHeight, setLocalHeight] = useBufferedState(height);

  const { handleImageDimensionChange, originalAspectRatio } = useAspectRatio({
    imageAspectRatioLock,
    blockContent: blockContent,
    sectionId: sectionId ?? '',
    blockId: blockId,
  });

  const updateWithoutUndo = useCallback(
    (updates: Partial<BlockSettings>) => {
      if (!blockId) return;
      const currentStyle = getBlockStyle();
      const nextStyle = { ...currentStyle, ...updates };
      blockDesignHandlerWithoutUndoRedo(blockId, currentStyle, nextStyle);
    },
    [blockId, getBlockStyle, blockDesignHandlerWithoutUndoRedo]
  );

  const handleFocus = useCallback(() => {
    saveCurrentStateToRef(getBlockStyle());
  }, [getBlockStyle, saveCurrentStateToRef]);

  const originalDimensionsRef = useRef<{ width: number; height: number } | null>(null);

  const handleDimensionFocus = useCallback(() => {
    originalDimensionsRef.current = { width, height };
  }, [width, height]);

  const handleOpacityChange = useCallback(
    (val) => {
      setLocalOpacity(val);
      updateWithoutUndo({ opacity: val });
    },
    [updateWithoutUndo]
  );

  const handleOpacityBlur = useCallback(
    (val) => {
      displayChange(selectedBlockIdByWrapper, {
        opacity: val,
        imageAlt: localImageAlt,
        imageLink: localImageLink,
      });
    },
    [selectedBlockIdByWrapper, displayChange, localImageAlt, localImageLink]
  );

  const handleImageWidthChange = (val: number) => {
    setLocalWidth(val);
    if (imageAspectRatioLock && originalAspectRatio) {
      setLocalHeight(val / originalAspectRatio);
    }
    handleImageDimensionChange(DIMENSION_WIDTH, val, false);
  };

  const handleImageWidthBlur = (val: number) => {
    if (originalDimensionsRef.current && val !== originalDimensionsRef.current.width) {
      handleImageDimensionChange(DIMENSION_WIDTH, val, true, originalDimensionsRef.current);
    }
    originalDimensionsRef.current = null;
  };

  const handleImageHeightChange = (val: number) => {
    setLocalHeight(val);
    if (imageAspectRatioLock && originalAspectRatio) {
      setLocalWidth(val * originalAspectRatio);
    }
    handleImageDimensionChange(DIMENSION_HEIGHT, val, false);
  };

  const handleImageHeightBlur = (val: number) => {
    if (originalDimensionsRef.current && val !== originalDimensionsRef.current.height) {
      handleImageDimensionChange(DIMENSION_HEIGHT, val, true, originalDimensionsRef.current);
    }
    originalDimensionsRef.current = null;
  };

  const handleImageAltChange = useCallback((val) => {
    setLocalImageAlt(val);
  }, []);

  const handleImageAltBlur = useCallback(
    (val) => {
      if (val !== imageAlt) {
        displayChange(selectedBlockIdByWrapper, {
          opacity,
          imageAlt: val,
          imageLink: localImageLink,
        });
      }
    },
    [selectedBlockIdByWrapper, displayChange, opacity, imageAlt, localImageLink]
  );

  const handleImageLinkChange = useCallback((val: string) => {
    setLocalImageLink(val);
  }, []);

  const handleImageLinkBlur = useCallback(
    (val: string) => {
      const newVal = cleanUrl(val);

      if (newVal !== imageLink) {
        displayChange(selectedBlockIdByWrapper, {
          opacity,
          imageAlt: localImageAlt,
          imageLink: newVal,
        });
      }
    },
    [selectedBlockIdByWrapper, displayChange, opacity, localImageAlt, imageLink]
  );

  const handleBackButtonClick = useCallback(() => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL }));
  }, [dispatch]);

  const handleBorderColorChange = useCallback(
    (color) => {
      if (color !== borderColor) {
        changeBlockBorderColor({ color, canBeUndo: false });
      }
    },
    [changeBlockBorderColor, borderColor]
  );

  const handleBorderSideChange = useCallback(
    (borderSide: string[]) => {
      const sides = getBlockSidesFromString(borderSide);
      if (JSON.stringify(sides) !== JSON.stringify(currentBorderSide)) {
        changeBlockBorderSide(sides);
      }
    },
    [currentBorderSide, changeBlockBorderSide]
  );

  const handleBorderWidthChange = useCallback((width) => {
    setLocalBorderWidth(width);
  }, []);

  const handleBorderWidthBlur = useCallback(
    (width) => {
      if (width !== borderWidth) {
        changeBorderWidth(width);
      }
    },
    [borderWidth, changeBorderWidth]
  );

  const handleBorderRadiusChange = useCallback((radius) => {
    setLocalBorderRadius(radius);
  }, []);

  const handleBorderRadiusBlur = useCallback(
    (radius) => {
      if (radius !== borderRadius) {
        changeBorderRadius({ radius });
      }
    },
    [borderRadius, changeBorderRadius]
  );

  return (
    <SecondaryMenu
      testId={'image-block-settings-component'}
      menuTitle={t('document.grid.image.design.title')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack direction="row" gap={2} alignItems="center">
        {blockContent?.content && (
          <ImagesLayout.GalleryTile size="small">
            <ImagesLayout.GalleryTileImage size="small" url={blockContent?.content} title={imageAlt} />
          </ImagesLayout.GalleryTile>
        )}
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL }));
            dispatch(setIsReplaceModeActive(true));
          }}
        >
          Replace
        </Button>
      </Stack>

      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.image.design.display')}</SecondaryMenuSectionTitle>
        <NumInputBoxTwoCol
          title={t('document.grid.image.design.opacity')}
          ariaLabel="%"
          inputValue={localOpacity}
          onChange={handleOpacityChange}
          onBlur={handleOpacityBlur}
          onFocus={handleFocus}
          limitedNum={100}
          disableNegativeNum
          testId="image-opacity-input"
        />

        <Stack gap={1} direction="row" alignItems="end">
          <Stack direction="row" gap={2}>
            <NumberInputBoxSingleCol
              title={t('document.grid.image.design.width')}
              onChange={handleImageWidthChange}
              onBlur={handleImageWidthBlur}
              onFocus={handleDimensionFocus}
              value={localWidth}
              endAdornmentText="px"
              testId="image-width-input"
            />
            <NumberInputBoxSingleCol
              title={t('document.grid.image.design.height')}
              onChange={handleImageHeightChange}
              onBlur={handleImageHeightBlur}
              onFocus={handleDimensionFocus}
              value={localHeight}
              endAdornmentText="px"
              testId="image-height-input"
            />
          </Stack>

          <IconButton data-testid="aspect-ratio-lock-button" onClick={() => changeAspectRatioLock(!imageAspectRatioLock)}>
            {imageAspectRatioLock ? <IcoLock /> : <IcoLockOpen />}
          </IconButton>
        </Stack>

        <TextInputBoxSingleCol
          title={t('document.grid.image.design.image_alt_text')}
          descriptionText={t('document.grid.image.design.image_alt_text_dec')}
          onChange={handleImageAltChange}
          onBlur={handleImageAltBlur}
          value={localImageAlt}
          testId="image-alt-input"
        />

        <TextInputBoxSingleCol
          title={t('document.grid.image.design.link')}
          descriptionText={t('document.grid.image.design.link_dec')}
          onChange={handleImageLinkChange}
          onBlur={handleImageLinkBlur}
          value={localImageLink}
          startAdornmentText={'https://'}
          testId="image-link-input"
        />
      </Stack>
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.image.design.border')}</SecondaryMenuSectionTitle>
        <ColorPickerSection
          currentColor={borderColor}
          onColorChange={handleBorderColorChange}
          onOpen={() => saveCurrentStateToRef(getBlockStyle())}
          onClose={(color) => changeBlockBorderColor({ color })}
        />
        <DropDownSection currentSelection={currentBorderSide} handleSelectionChange={handleBorderSideChange} testId={'border-dropdown'} />
        <NumInputBoxTwoCol
          inputValue={localBorderWidth}
          onChange={handleBorderWidthChange}
          onBlur={handleBorderWidthBlur}
          title={t('document.grid.text.design.border_width')}
          testId={'border-width'}
          disableNegativeNum
        />
        <NumInputBoxTwoCol
          title={t('document.grid.text.design.border_radius')}
          inputValue={localBorderRadius}
          disableNegativeNum
          onChange={handleBorderRadiusChange}
          onBlur={handleBorderRadiusBlur}
          testId={'border-radius'}
        />

        <SecondaryMenuTrigger
          ButtonText={t('document.grid.text.advanced_spacing.title')}
          ButtonDesc={t('document.grid.text.advanced_spacing.adjust_left_top_right_bottom_block_padding')}
          handleClick={() => {
            dispatch(setIsAdvancedSpacingModelOpen(true));
          }}
        />
      </Stack>
    </SecondaryMenu>
  );
}
